import { useSaldoResumo } from '../../useSaldoResumo';
import { dataDoDia, moeda } from '../../formato';
import type { WizardState } from './types';

interface LinhaResumo {
  rotulo: string;
  valor: string | null;
}

function linhasResumo(estado: WizardState): LinhaResumo[] {
  return [
    { rotulo: 'CONDUTOR', valor: estado.condutorNome || null },
    { rotulo: 'VEÍCULO', valor: estado.placa || null },
    {
      rotulo: 'ROTA',
      valor: estado.municipioOrigemNome && estado.municipioDestinoNome
        ? `${estado.municipioOrigemNome} → ${estado.municipioDestinoNome}`
        : null,
    },
    { rotulo: 'DATAS', valor: estado.dataIda ? `${dataDoDia(estado.dataIda)} ${estado.horarioIda}` : null },
    { rotulo: 'TRAJETO', valor: estado.kmIda > 0 ? `${estado.kmIda} km` : null },
    { rotulo: 'PASSAGEIROS', valor: estado.passageiros.length > 0 ? `${estado.passageiros.length}` : null },
    { rotulo: 'VALOR DECLARADO', valor: estado.valorCentavos > 0 ? moeda(estado.valorCentavos / 100) : null },
  ];
}

interface Props {
  passoAtual: number;
  estado: WizardState;
  tituloRevisao?: boolean;
}

/**
 * Rail (resumo + custo) — handoff §2. Rail e card de revisão leem a mesma
 * fonte de dados (o `estado` do wizard) — nunca podem divergir.
 */
export function WizardRail({ passoAtual, estado, tituloRevisao }: Props) {
  const { saldo } = useSaldoResumo();
  const linhas = linhasResumo(estado);
  // O débito real é recalculado no servidor no momento da emissão; aqui é só
  // a previsão a partir do valor declarado.
  const debitoEstimado = estado.valorCentavos > 0 ? estado.valorCentavos / 100 : null;
  const saldoApos = saldo && debitoEstimado != null ? saldo.saldoDisponivel - debitoEstimado : null;
  const faltam = saldoApos != null && saldoApos < 0 ? Math.abs(saldoApos) : null;

  return (
    <aside className="co-trilho" aria-label="Resumo da emissão">
      {saldo && (
        <div className="co-cartao">
          <span className="co-rotulo">Saldo</span>
          <div className="co-trilho-valor">
            {moeda(saldo.saldoDisponivel)}
            {saldo.emissoesMinimas != null && ` · pelo menos ${saldo.emissoesMinimas} emissões`}
          </div>
        </div>
      )}

      <div className="co-cartao">
        <h3 className="co-rotulo">{tituloRevisao ? 'O que vai ser solicitado' : 'Resumo da emissão'}</h3>
        {linhas.map((l, i) => (
          <div key={l.rotulo} className="co-trilho-linha" aria-current={i === passoAtual ? 'step' : undefined}>
            <span className="co-rotulo">{l.rotulo}</span>
            <div className={`co-trilho-valor${l.valor ? '' : ' co-trilho-valor-vazio'}`}>{l.valor ?? 'A definir'}</div>
          </div>
        ))}
      </div>

      {debitoEstimado != null && (
        <div className="co-cartao">
          <span className="co-rotulo">{saldo ? 'Débito estimado' : 'Valor declarado'}</span>
          <div className="co-trilho-grande">{moeda(debitoEstimado)}</div>
          {saldoApos != null && (
            <div className="co-trilho-linha">
              <span className="co-rotulo">Saldo após</span>
              <div className="co-trilho-grande">
                {faltam ? `Faltam ${moeda(faltam)}` : moeda(saldoApos)}
              </div>
            </div>
          )}
          {faltam && (
            <p className="co-apoio">Você monta a emissão inteira; o crédito só é exigido ao solicitar.</p>
          )}
        </div>
      )}
    </aside>
  );
}
